import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { updatePassword } from '../api/user';

export default function ChangePassword() {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const { user } = useSelector((state) => state.user);

 // 表单状态
 const [formData, setFormData] = useState({
 newPassword: '',
 confirmPassword: ''
 });

 // UI状态
 const [showPasswords, setShowPasswords] = useState({
 new: false,
 confirm: false
 });
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');
 const [success, setSuccess] = useState(false);


 // 处理输入变化
 const handleInputChange = (e) => {
 const { name, value } = e.target;
 setFormData(prev => ({
 ...prev,
 [name]: value
 }));
 setError('');
 };

 // 切换密码可见性
 const togglePasswordVisibility = (field) => {
 setShowPasswords(prev => ({
 ...prev,
 [field]: !prev[field]
 }));
 };

 // 验证表单
 const validateForm = () => {
 if (!formData.newPassword) {
 setError(t('changePassword.errors.newPasswordRequired'));
 return false;
 }

 if (formData.newPassword !== formData.confirmPassword) {
 setError(t('changePassword.errors.passwordMismatch'));
 return false;
 }

 return true;
 };

 // 提交表单
 const handleSubmit = async (e) => {
 e.preventDefault();
 
 if (!validateForm()) return;

 setLoading(true);
 setError('');

 try {
 // 获取当前用户的phoneNumbers (email或uid)
 const phoneNumbers = user?.email || user?.uid;
 
 if (!phoneNumbers) {
 setError(t('changePassword.errors.userNotFound'));
 return;
 }

 // 调用API修改密码
 const response = await updatePassword({
 phoneNumbers: phoneNumbers,
 password: formData.newPassword
 });

 if (response.code === 1) {
 setSuccess(true);
 setTimeout(() => {
 navigate('/settings');
 }, 3000);
 } else {
 setError(response.message || t('changePassword.errors.changeFailed'));
 }
 } catch (error) {
 console.error('修改密码失败:', error);
 setError(error.response?.data?.message || t('changePassword.errors.changeFailed'));
 } finally {
 setLoading(false);
 }
 };

 // 如果修改成功，显示成功页面
 if (success) {
 return (
 <div className="min-h-screen bg-gradient-to-br from-accent-50 via-white to-amber-50 py-8 px-4">
 <div className="max-w-md mx-auto">
 <div className="bg-white rounded-2xl shadow-xl p-8">
 <div className="flex flex-col items-center">
 <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
 <CheckCircle className="w-10 h-10 text-white" />
 </div>
 <h2 className="text-2xl font-bold text-ink-900 mb-2">
 {t('changePassword.success.title')}
 </h2>
 <p className="text-ink-600 text-center">
 {t('changePassword.success.message')}
 </p>
 <p className="text-sm text-ink-500 mt-4">
 {t('changePassword.success.redirecting')}
 </p>
 </div>
 </div>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-gradient-to-br from-accent-50 via-white to-amber-50 py-8 px-4">
 <div className="max-w-md mx-auto">
 {/* 页面标题 */}
 <div className="text-center mb-8">
 <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-2xl mb-4">
 <Lock className="w-8 h-8 text-accent-600" />
 </div>
 <h1 className="text-3xl font-bold text-ink-900 mb-2">
 {t('changePassword.title')}
 </h1>
 <p className="text-ink-600">
 {t('changePassword.subtitle')}
 </p>
 </div>

 {/* 表单 */}
 <div className="bg-white rounded-2xl shadow-xl p-8">
 <form onSubmit={handleSubmit} className="space-y-6">
 {/* 错误提示 */}
 {error && (
 <div className="bg-red-50 border border-red-200 rounded-lg p-4">
 <div className="flex items-center gap-2">
 <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
 <span className="text-red-800 text-sm">{error}</span>
 </div>
 </div>
 )}


 {/* 新密码 */}
 <div>
 <label htmlFor="newPassword" className="block text-sm font-medium text-ink-700 mb-2">
 {t('changePassword.form.newPassword')}
 </label>
 <div className="relative">
 <input
 type={showPasswords.new ? 'text' : 'password'}
 id="newPassword"
 name="newPassword"
 value={formData.newPassword}
 onChange={handleInputChange}
 className="w-full px-4 py-3 pr-12 border border-ink-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
 placeholder={t('changePassword.form.newPasswordPlaceholder')}
 disabled={loading}
 />
 <button
 type="button"
 onClick={() => togglePasswordVisibility('new')}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-700"
 >
 {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
 </button>
 </div>
 </div>

 {/* 确认新密码 */}
 <div>
 <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-700 mb-2">
 {t('changePassword.form.confirmPassword')}
 </label>
 <div className="relative">
 <input
 type={showPasswords.confirm ? 'text' : 'password'}
 id="confirmPassword"
 name="confirmPassword"
 value={formData.confirmPassword}
 onChange={handleInputChange}
 className="w-full px-4 py-3 pr-12 border border-ink-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
 placeholder={t('changePassword.form.confirmPasswordPlaceholder')}
 disabled={loading}
 />
 <button
 type="button"
 onClick={() => togglePasswordVisibility('confirm')}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-700"
 >
 {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
 </button>
 </div>
 {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
 <p className="mt-1 text-sm text-red-600">
 {t('changePassword.errors.passwordMismatch')}
 </p>
 )}
 </div>

 {/* 提交按钮 */}
 <div className="flex gap-4">
 <button
 type="button"
 onClick={() => navigate('/settings')}
 className="flex-1 px-6 py-3 border border-ink-300 text-ink-700 rounded-lg font-semibold hover:bg-cream-100 transition-colors"
 disabled={loading}
 >
 {t('common.cancel')}
 </button>
 <button
 type="submit"
 className="flex-1 bg-accent-500 text-white py-3 rounded-lg font-semibold hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
 disabled={loading}
 >
 {loading ? (
 <>
 <Loader2 className="w-5 h-5 animate-spin" />
 {t('common.processing')}
 </>
 ) : (
 t('changePassword.form.submit')
 )}
 </button>
 </div>
 </form>

 </div>
 </div>
 </div>
 );
}